import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  User, 
  Mail, 
  Lock, 
  Sun, 
  Moon, 
  Trash2, 
  CreditCard,
  AlertTriangle,
  Loader2,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, isSubscriber } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  const [pricing, setPricing] = useState({ subscription_price: 9.99 });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Fetch pricing
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            subscription_price: data.subscription?.price || 9.99
          });
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to change password');
      }

      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/account`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Account deleted');
        await logout();
        navigate('/');
      } else {
        throw new Error('Failed to delete account');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="border-b border-border">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
              Settings
            </h1>
            <p className="text-muted-foreground">Manage your account preferences</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Profile */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-5 h-5 text-muted-foreground" />
              <h2 className="font-display text-lg font-bold uppercase tracking-tight">Profile</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                    <span className="font-display font-bold text-xl">{user?.name?.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-lg">{user?.name}</p>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              {isSubscriber && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-sm">
                  <CreditCard className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Premium Subscriber</span>
                </div>
              )}
            </div>
          </div>

          {/* Appearance */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              {isDark ? <Moon className="w-5 h-5 text-muted-foreground" /> : <Sun className="w-5 h-5 text-muted-foreground" />}
              <h2 className="font-display text-lg font-bold uppercase tracking-tight">Appearance</h2>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">Toggle between light and dark theme</p>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </div>

          {/* Change Password */}
          {user?.password_hash !== null && (
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">Change Password</h2>
              </div>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showPasswords ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="rounded-sm pr-10"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="rounded-sm"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="rounded-sm"
                    required
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="rounded-sm"
                  disabled={changingPassword}
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Password
                </Button>
              </form>
            </div>
          )}

          {/* Subscription */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              <h2 className="font-display text-lg font-bold uppercase tracking-tight">Subscription</h2>
            </div>
            {isSubscriber ? (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  You have an active premium subscription with access to all features.
                </p>
                <Button 
                  variant="outline" 
                  className="rounded-sm"
                  onClick={() => navigate('/billing')}
                >
                  Manage Subscription
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Upgrade to premium for access to detailed trends, head-to-head analysis, 
                  historical data exports, and more.
                </p>
                <Button 
                  className="btn-blaze rounded-sm"
                  onClick={() => navigate('/billing')}
                  data-testid="upgrade-btn"
                >
                  Upgrade to Premium - ${pricing.subscription_price?.toFixed(2) || '9.99'}/month
                </Button>
              </div>
            )}
          </div>

          {/* Delete Account */}
          <div className="bg-card border border-red-500/30 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h2 className="font-display text-lg font-bold uppercase tracking-tight text-red-400">
                Danger Zone
              </h2>
            </div>
            {!showDeleteConfirm ? (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button 
                  variant="destructive"
                  className="rounded-sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-400">
                    Are you absolutely sure? This will permanently delete your account, 
                    all your alerts, and subscription data.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="destructive"
                    className="rounded-sm"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    data-testid="confirm-delete-btn"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Yes, Delete My Account
                  </Button>
                  <Button 
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
